import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatSkeleton } from '@anytag/shared-atoms';
import { UseInitAIAssistant } from '../hook';
import { AI, InputProps } from './components';
import { ChatRoomProvider } from './chat-room-context';
import { useAIAssistantUploadFilesToGCS } from './hook';

export const ChatRoom = ({
  error,
  threadId,
  messages,
  setError,
  addNewMessages,
  isCreatingChatAi,
  isSendingMessage,
  defaultTemplate,
  createChatAiThread,
  getChatHistory,
  regenerateMessage,
  regenerateResponse,
  setDefaultTemplate,
  chatHistoryThreadId,
  isSupportedChatAIPaths,
  isLoadingChatHistory
}: UseInitAIAssistant) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [canScrollToBottom, setCanScrollToBottom] = useState(true);
  const ref = useRef<{ message: string; files: File[]; callback: () => void } | null>(null);
  const { uploading, handleUploadFiles } = useAIAssistantUploadFilesToGCS({ setError });

  // Waiting for threadId is ready to send the 1st message
  useEffect(() => {
    if (threadId && ref.current) {
      handleSubmit({ message: ref.current.message, files: ref.current.files }, ref.current.callback);
      ref.current = null;
    }
  }, [threadId, ref.current]);

  const handleSubmit: InputProps['onSubmit'] = ({ message, files }, callback) => {
    // Don't combine 2 conditions below cause make wrong display text & images position
    if (files.length) {
      handleUploadFiles(files).then((fileUrls) => {
        const imageUrl = fileUrls?.at(0); // currently we only support upload 1 image

        addNewMessages([
          {
            text: message,
            position: 'right',
            file: imageUrl ? { type: 'image', url: imageUrl } : undefined
          }
        ]);

        callback();
      });
    } else if (message) {
      addNewMessages([{ position: 'right', text: message }]);
      callback();
    } else if (defaultTemplate) {
      setDefaultTemplate('');
    }
  };

  if (!isSupportedChatAIPaths) {
    return <div className="px-6 py-12 text-base font-bold">{t('chatAiButtonDisabledHelp')}</div>;
  }

  if (isLoadingChatHistory) {
    return <ChatSkeleton />;
  }

  return (
    <ChatRoomProvider>
      <AI.Body>
        {error ? (
          <AI.Errors {...error} onRegenerateResponse={regenerateResponse} />
        ) : messages.length ? (
          <AI.Messages
            messages={messages}
            getChatHistory={getChatHistory}
            onRegenerate={regenerateMessage}
            chatHistoryThreadId={chatHistoryThreadId}
            canScrollToBottom={canScrollToBottom}
            isLoadingChatHistory={isLoadingChatHistory}
          />
        ) : !defaultTemplate && !isSendingMessage && !isLoadingChatHistory ? (
          <AI.Templates
            onSelect={(template) => {
              setDefaultTemplate(template);
              // Auto focus to input after select a template
              // so user can start typing immediately without any click
              inputRef.current?.focus();
            }}
          />
        ) : null}
      </AI.Body>

      <AI.Input
        ref={inputRef}
        loading={uploading || isCreatingChatAi || isSendingMessage}
        defaultMessage={defaultTemplate}
        onSubmit={({ message, files }, callback) => {
          // Create chat threadId on 1st message which sent by user
          if (!threadId) {
            createChatAiThread();
            ref.current = { message, files, callback }; // Cache data then send the message when threadId is ready
          } else {
            handleSubmit({ message, files }, callback);
          }

          // canScrollToBottom prevented when loading chat history, we need to reset it to make messages scrollable to bottom when have new changes
          if (!canScrollToBottom) {
            setCanScrollToBottom(true);
          }
        }}
      />
    </ChatRoomProvider>
  );
};
